import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, shareReplay } from 'rxjs';

// Re-export models for consumers
import { Employee } from '../../models/employee.model';
import { Customer } from '../../models/customer.model';
import { Project } from '../../models/project.model';
import { Activity } from '../../models/activity.model';
import { Team, TeamConsistencyIssue, TeamConsistencyResult } from '../../models/team.model';
import {
  EmployeeSalary,
  EmployeeSalaryConsistencyIssue,
  EmployeeSalaryConsistencyResult,
  EmployeeSalaryMassUpdateRowInput,
  EmployeeSalaryMassUpdateResult,
} from '../../models/employee-salary.model';
import { Unit } from '../../models/unit.model';
import { Costcenter } from '../../models/costcenter.model';
import { AuditLogEntry, AuditLogFilters, AuditLogResult } from '../../models/audit-log.model';

export {
  Employee,
  Customer,
  Project,
  Activity,
  Team,
  TeamConsistencyIssue,
  TeamConsistencyResult,
  EmployeeSalary,
  EmployeeSalaryConsistencyIssue,
  EmployeeSalaryConsistencyResult,
  EmployeeSalaryMassUpdateRowInput,
  EmployeeSalaryMassUpdateResult,
  Unit,
  Costcenter,
  AuditLogEntry,
  AuditLogFilters,
  AuditLogResult,
};

export interface ActivityNewPricePayload {
  new_name: string;
  new_price: number;
  new_start_date: string;
}

export interface ActivityNewPriceResult {
  old_activity_id: number;
  new_activity_id: number;
  old_stop_date: string;
  new_start_date: string;
  moved_timerecords: number;
}

export interface ProjectBulkCreatePayload {
  project: {
    customer_id: number;
    project_name: string;
    external_project_name?: string | null;
    endcustomer?: string | null;
    extprojectid?: string | null;
    ourreference?: string | null;
    extreference?: string | null;
    addinfo?: string | null;
    invoice_lvl?: string | null;
    budget_hours?: number | null;
  };
  participants: Array<{
    employee_id: number;
    price: number;
    unit_id: number;
    costcenter?: string | null;
  }>;
  activity_templates: Array<{ activity_name: string }>;
  start_date: string;
}

export interface ProjectBulkCreateResult {
  project_id: number;
  created_activities: number;
  activity_ids: number[];
}

// ============================================
// Service
// ============================================

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  private units$?: Observable<Unit[]>;
  private costcenters$?: Observable<Costcenter[]>;

  constructor(@Inject(HttpClient) private http: HttpClient) {}

  // ----------------------------------------
  // Employees
  // ----------------------------------------
  getEmployees(): Observable<Employee[]> {
    return this.http.get<Employee[]>('api/employees');
  }

  createEmployee(data: Partial<Employee>): Observable<any> {
    return this.http.post('api/employees', data);
  }

  updateEmployee(id: number, data: Partial<Employee>): Observable<any> {
    return this.http.put(`api/employees/${id}`, data);
  }

  deleteEmployee(id: number): Observable<any> {
    return this.http.delete(`api/employees/${id}`);
  }

  // ----------------------------------------
  // Customers
  // ----------------------------------------
  getCustomers(): Observable<Customer[]> {
    return this.http.get<Customer[]>('api/customers');
  }

  createCustomer(data: Partial<Customer>): Observable<any> {
    return this.http.post('api/customers', data);
  }

  updateCustomer(id: number, data: Partial<Customer>): Observable<any> {
    return this.http.put(`api/customers/${id}`, data);
  }

  deleteCustomer(id: number): Observable<any> {
    return this.http.delete(`api/customers/${id}`);
  }

  // ----------------------------------------
  // Employee salaries
  // ----------------------------------------
  getEmployeeSalaries(employeeId: number): Observable<EmployeeSalary[]> {
    return this.http.get<EmployeeSalary[]>(`api/employee-salaries/${employeeId}`);
  }

  getEmployeeSalaryConsistency(): Observable<EmployeeSalaryConsistencyResult> {
    return this.http.get<EmployeeSalaryConsistencyResult>('api/employee-salaries/consistency');
  }

  createEmployeeSalary(data: Partial<EmployeeSalary>): Observable<any> {
    return this.http.post('api/employee-salaries', data);
  }

  updateEmployeeSalary(id: number, data: Partial<EmployeeSalary>): Observable<any> {
    return this.http.put(`api/employee-salaries/${id}`, data);
  }

  deleteEmployeeSalary(id: number): Observable<any> {
    return this.http.delete(`api/employee-salaries/${id}`);
  }

  previewEmployeeSalaryMassUpdate(rows: EmployeeSalaryMassUpdateRowInput[]): Observable<EmployeeSalaryMassUpdateResult> {
    return this.http.post<EmployeeSalaryMassUpdateResult>('api/employee-salaries/mass-update', {
      dry_run: true,
      rows,
    });
  }

  applyEmployeeSalaryMassUpdate(rows: EmployeeSalaryMassUpdateRowInput[]): Observable<EmployeeSalaryMassUpdateResult> {
    return this.http.post<EmployeeSalaryMassUpdateResult>('api/employee-salaries/mass-update', {
      dry_run: false,
      rows,
    });
  }

  // ----------------------------------------
  // Project bulk create
  // ----------------------------------------
  bulkCreateProject(data: ProjectBulkCreatePayload): Observable<ProjectBulkCreateResult> {
    return this.http.post<ProjectBulkCreateResult>('api/project-bulk-create', data);
  }

  // ----------------------------------------
  // Teams
  // ----------------------------------------
  getTeams(): Observable<Team[]> {
    return this.http.get<Team[]>('api/teams');
  }

  getTeamConsistency(): Observable<TeamConsistencyResult> {
    return this.http.get<TeamConsistencyResult>('api/teams/consistency');
  }

  createTeam(data: Partial<Team>): Observable<any> {
    return this.http.post('api/teams', data);
  }

  updateTeam(id: number, data: Partial<Team>): Observable<any> {
    return this.http.put(`api/teams/${id}`, data);
  }

  deleteTeam(id: number): Observable<any> {
    return this.http.delete(`api/teams/${id}`);
  }

  // ----------------------------------------
  // Projects
  // ----------------------------------------
  getProjects(): Observable<Project[]> {
    return this.http.get<Project[]>('api/projectss');
  }

  createProject(data: Partial<Project>): Observable<any> {
    return this.http.post('api/projectss', data);
  }

  updateProject(id: number, data: Partial<Project>): Observable<any> {
    return this.http.put(`api/projectss/${id}`, data);
  }

  deleteProject(id: number): Observable<any> {
    return this.http.delete(`api/projectss/${id}`);
  }

  // ----------------------------------------
  // Activities
  // ----------------------------------------
  getActivities(): Observable<Activity[]> {
    return this.http.get<Activity[]>('api/activities');
  }

  createActivity(data: Partial<Activity>): Observable<any> {
    return this.http.post('api/activities', data);
  }

  updateActivity(id: number, data: Partial<Activity>): Observable<any> {
    return this.http.put(`api/activities/${id}`, data);
  }

  newPriceActivity(id: number, data: ActivityNewPricePayload): Observable<ActivityNewPriceResult> {
    return this.http.post<ActivityNewPriceResult>(`api/activities/${id}/new-price`, data);
  }

  deleteActivity(id: number): Observable<any> {
    return this.http.delete(`api/activities/${id}`);
  }

  // ----------------------------------------
  // Units (read-only)
  // ----------------------------------------
  getUnits(): Observable<Unit[]> {
    if (!this.units$) {
      this.units$ = this.http.get<Unit[]>('api/units').pipe(shareReplay(1));
    }
    return this.units$;
  }

  // ----------------------------------------
  // Costcenters (reference data)
  // ----------------------------------------
  getCostcenters(): Observable<Costcenter[]> {
    if (!this.costcenters$) {
      this.costcenters$ = this.http.get<Costcenter[]>('api/costcenters').pipe(shareReplay(1));
    }
    return this.costcenters$;
  }

  // ----------------------------------------
  // Audit log
  // ----------------------------------------
  getAuditEntries(filters: AuditLogFilters): Observable<AuditLogResult> {
    return this.http.get<AuditLogResult>('api/audit', {
      params: Object.entries(filters).reduce((params, [key, value]) => {
        if (value === null || value === undefined || value === '') {
          return params;
        }
        return {
          ...params,
          [key]: String(value),
        };
      }, {} as Record<string, string>),
    });
  }
}
